import React from 'react';
import { FormattedMessage, intlShape } from 'react-intl';
import { array, bool, func, number, object, string } from 'prop-types';
import { Box, Heading, Stepper, Text } from '@landtrustinc/design-system';
import InquiryFlowChrome from './InquiryFlowChrome';
import InquiryWizardStepSignup from './steps/InquiryWizardStepSignup';
import InquiryTripSummary from './InquiryTripSummary';
import InquiryPricingDetails from './InquiryPricingDetails';
import InquiryTripDetailsForm from './InquiryTripDetailsForm';
import InquiryTripActions from './InquiryTripActions';
import InquirySidebarEyebrow from './InquirySidebarEyebrow';
import useInquiryPricing from './hooks/useInquiryPricing';
import useInquiryDates from './hooks/useInquiryDates';
import useInquiryPackageSummary from './hooks/useInquiryPackageSummary';
import useInquiryLodging from './hooks/useInquiryLodging';
import useInquiryPackageActions from './hooks/useInquiryPackageActions';
import { useInquiryWizard } from './InquiryWizardContext';
import InquiryEmailGate from './InquiryEmailGate';
import { hasSavedInquiryDraft, isInquiryEmailUnverified } from './inquiryAccess';
import { describeInquiryItinerary } from './inquiryPackageModel';
import css from './InquiryPackageFlow.module.css';

const InquiryPackageFlow = ({
  intl,
  listing,
  timeSlots,
  form,
  values,
  invalid,
  disabled,
  currentUser,
  history,
  params,
  sendEnquiryInProgress,
  followSubjectMutation,
  errorMessage,
  isDesktop,
  isInquirySubmitLocked,
  onSubmitInquiry,
  inquirySubmitAttempt,
}) => {
  const { steps, step, next, isLast } = useInquiryWizard();
  const selectedPackage = values?.selectedPackage;
  const { startDate, endDate } = values?.bookingDates || {};
  const summary = useInquiryPackageSummary({ listing, formValues: values, intl });
  const pricingView = useInquiryPricing({ listing, formValues: values, intl });
  const dates = useInquiryDates({ intl, startDate, endDate });
  const lodging = useInquiryLodging({
    listing,
    selectedPackage,
    timeSlots,
    intl,
    lodgingAddons: values?.lodgingAddons,
    startDate,
    endDate,
    isSingleDay: dates.isSingleDay,
  });
  const actions = useInquiryPackageActions({
    steps,
    step,
    next,
    listing,
    selectedPackage,
    params,
    currentUser,
    followSubjectMutation,
    invalid,
    disabled,
    startDate,
    endDate,
    message: values?.message,
    partySize: values?.partySize,
    form,
  });
  const itinerary = describeInquiryItinerary({
    listing,
    activity: selectedPackage?.activity,
    startDate,
    endDate,
    startLodgingDate: values?.bookingDates?.startLodgingDate,
    hasLodging: lodging.dynamicHasLodging,
  });
  const emailUnverified = isInquiryEmailUnverified(currentUser);
  const showPricing = Boolean(pricingView) && (actions.isTripDetails || actions.isAccountStep);
  const editUrl = actions.isAccountStep && actions.tripStep ? actions.tripStep.url : null;
  const onEdit = editUrl ? () => history.push(editUrl) : null;
  const editLabel = intl.formatMessage({ id: 'InquiryWizard.edit' });
  const renderSummary = embedPricing => (
    <InquiryTripSummary
      summary={summary}
      pricingView={pricingView}
      showPricing={showPricing}
      embedPricing={embedPricing}
      editLabel={editLabel}
      onEdit={onEdit}
      itinerary={itinerary}
    />
  );

  return (
    <InquiryFlowChrome
      backLinkName="ListingPage"
      backLinkParams={{ id: actions.packageParams.id, slug: actions.packageParams.slug }}
      onBack={onEdit}
      backLabel={intl.formatMessage({ id: 'InquiryWizard.backToTripDetails' })}
    >
      {actions.showStepper ? (
        <div className={css.stepper}>
          <Stepper steps={actions.stepperSteps} currentStep={step?.id || 0} variant="auto" />
        </div>
      ) : null}

      <div className={css.layout}>
        <div className={css.main}>
          {!actions.isAccountStep ? (
            <div className={css.summaryMobile}>{renderSummary(false)}</div>
          ) : null}

          {actions.isTripDetails && emailUnverified ? (
            <InquiryEmailGate
              currentUser={currentUser}
              saved={hasSavedInquiryDraft(values)}
              landownerName={summary.landownerName}
            />
          ) : null}

          {actions.isTripDetails && !emailUnverified && selectedPackage && (
            <>
              <div className={css.intro}>
                <Box mb="var(--spacing-3)">
                  <Heading
                    as="h1"
                    size="2xs"
                    fontWeight="extrabold"
                    color="var(--color-primary-800)"
                  >
                    <FormattedMessage
                      id="InquiryWizard.tripDetailsTitle"
                      values={{ name: summary.landownerName }}
                    />
                  </Heading>
                </Box>
                <Text size="md" color="var(--color-hunter-500)">
                  <FormattedMessage
                    id="InquiryWizard.tripDetailsSubtitle"
                    values={{ name: summary.landownerName }}
                  />
                </Text>
              </div>
              <InquiryTripDetailsForm
                intl={intl}
                form={form}
                values={values}
                selectedPackage={selectedPackage}
                dates={dates}
                lodging={lodging}
                timeSlots={timeSlots}
                firstName={summary.firstName}
                validationErrors={actions.validationErrors}
              />
              {!isDesktop && showPricing ? (
                <div className={css.mobilePricing}>
                  <InquiryPricingDetails pricingView={pricingView} />
                </div>
              ) : null}
              <InquiryTripActions
                errorMessage={errorMessage}
                submitDisabled={actions.submitDisabled}
                isLast={isLast}
                sendEnquiryInProgress={sendEnquiryInProgress}
                isInquirySubmitLocked={isInquirySubmitLocked}
                onContinue={actions.onContinue}
                onFollow={actions.followProperty}
                onValidate={actions.validateTripDetails}
                showValidationSummary={actions.showValidationSummary}
                landownerName={summary.landownerName}
              />
            </>
          )}

          {actions.isAccountStep && (
            <InquiryWizardStepSignup
              listing={listing}
              formValues={values}
              currentUser={currentUser}
              errorMessage={errorMessage}
              inquirySubmitInFlight={sendEnquiryInProgress}
              inquirySubmitAttempt={inquirySubmitAttempt}
              onSubmitInquiry={onSubmitInquiry}
            />
          )}
        </div>

        <aside className={css.sidebar}>
          <InquirySidebarEyebrow messageId="InquiryWizard.yourTrip" />
          {renderSummary(isDesktop)}
        </aside>
      </div>
    </InquiryFlowChrome>
  );
};

InquiryPackageFlow.defaultProps = {
  timeSlots: null,
  currentUser: null,
  values: null,
  errorMessage: null,
  invalid: false,
  disabled: false,
  isDesktop: false,
  isInquirySubmitLocked: () => false,
  onSubmitInquiry: null,
  inquirySubmitAttempt: 0,
};

InquiryPackageFlow.propTypes = {
  intl: intlShape.isRequired,
  listing: object.isRequired,
  timeSlots: array,
  form: object.isRequired,
  values: object,
  invalid: bool,
  disabled: bool,
  currentUser: object,
  history: object.isRequired,
  params: object.isRequired,
  sendEnquiryInProgress: bool.isRequired,
  followSubjectMutation: func.isRequired,
  errorMessage: string,
  isDesktop: bool,
  isInquirySubmitLocked: func,
  onSubmitInquiry: func,
  inquirySubmitAttempt: number,
};

export default InquiryPackageFlow;
